'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import { usePathname } from 'next/navigation';
import { ArrowRight, Clock, BookOpen, Trash2, Newspaper, RefreshCw, Archive } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface StoryMeta {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  body?: string;
  category: string;
  time: string;
  image: string;
  alt: string;
  href?: string;
  fetched_at?: string;
}

// ─── Static fallback stories ──────────────────────────────────────────────────
export const WORLD_CUP_STORIES: StoryMeta[] = [];

// ─── History ──────────────────────────────────────────────────────────────────
const HISTORY_KEY = 'wc_news_history';

export interface HistoryEntry {
  slug: string;
  title: string;
  image: string;
  alt: string;
  visitedAt: string;
}

function useReadHistory() {
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) setHistory(JSON.parse(raw));
    } catch {
      // ignore
    }
  }, []);

  const clearHistory = () => {
    localStorage.removeItem(HISTORY_KEY);
    setHistory([]);
  };

  return { history, clearHistory };
}

// ─── Group stories by fetch date ──────────────────────────────────────────────
function groupByDate(stories: StoryMeta[]): { label: string; stories: StoryMeta[] }[] {
  const groups: Record<string, StoryMeta[]> = {};
  for (const story of stories) {
    const dateKey = story.fetched_at
      ? new Date(story.fetched_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : 'Today';
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(story);
  }
  return Object.entries(groups).map(([label, stories]) => ({ label, stories }));
}

// ─── Story Card ───────────────────────────────────────────────────────────────
function StoryCard({ story, isRead }: { story: StoryMeta; isRead: boolean }) {
  const { t } = useLanguage();
  return (
    <Link
      href={`/news/${story.slug}`}
      className="group rounded-2xl overflow-hidden flex flex-col sm:flex-row transition-all duration-200 hover:scale-[1.01]"
      style={{
        background: 'hsl(222 35% 11%)',
        border: `1px solid ${isRead ? 'hsl(222 25% 20%)' : 'hsl(222 25% 18%)'}`,
        boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
        opacity: isRead ? 0.78 : 1,
      }}
    >
      <div className="relative overflow-hidden flex-shrink-0 h-52 sm:h-auto sm:w-56">
        <img
          src={story.image}
          alt={story.alt}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to right, transparent 60%, hsl(222 35% 11% / 0.5) 100%)' }}
        />
        <span
          className="absolute top-3 left-3 text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider"
          style={{ background: 'hsl(25 95% 53% / 0.95)', color: 'white', backdropFilter: 'blur(4px)' }}
        >
          {story.category}
        </span>
        {isRead && (
          <span
            className="absolute top-3 right-3 text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1"
            style={{ background: 'hsl(222 40% 9% / 0.85)', color: 'hsl(222 15% 55%)' }}
          >
            <BookOpen size={10} /> {t.news.read}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-3 flex-1 p-5 justify-center">
        <h3 className="font-bold text-white leading-snug group-hover:text-orange-400 transition-colors text-base">
          {story.title}
        </h3>
        <p className="text-sm leading-relaxed line-clamp-2" style={{ color: 'hsl(222 15% 65%)' }}>
          {story.excerpt}
        </p>
        <div
          className="flex items-center justify-between mt-auto pt-3 border-t"
          style={{ borderColor: 'hsl(222 25% 18%)' }}
        >
          <span className="text-xs font-medium flex items-center gap-1.5" style={{ color: 'hsl(222 15% 50%)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 inline-block" />
            {story.time}
          </span>
          <span className="text-xs font-semibold text-orange-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {t.news.readStory} <ArrowRight size={12} />
          </span>
        </div>
      </div>
    </Link>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function StorySkeleton() {
  return (
    <div
      className="rounded-2xl overflow-hidden flex flex-col sm:flex-row animate-pulse"
      style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 18%)', height: '160px' }}
    >
      <div className="flex-shrink-0 h-full sm:w-56" style={{ background: 'hsl(222 35% 15%)' }} />
      <div className="flex flex-col gap-3 flex-1 p-5 justify-center">
        <div className="h-4 rounded-lg w-3/4" style={{ background: 'hsl(222 35% 16%)' }} />
        <div className="h-3 rounded-lg w-full" style={{ background: 'hsl(222 35% 14%)' }} />
        <div className="h-3 rounded-lg w-2/3" style={{ background: 'hsl(222 35% 14%)' }} />
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function NewsPage() {
  const pathname = usePathname();
  const { history, clearHistory } = useReadHistory();
  const { t, language } = useLanguage();
  const [stories, setStories] = useState<StoryMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const readSlugs = new Set(history.map((h) => h.slug));

  const fetchStories = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      // On manual refresh, bust the server-side cache so fresh data is fetched
      // Pass lang param so Perplexity returns content in the correct language
      const langParam = language === 'es' ? '&lang=es' : '';
      const url = isRefresh ? `/api/news?bust=true${langParam}` : `/api/news?lang=${language}`;
      const res = await fetch(url);
      const data = await res.json();
      if (Array.isArray(data.articles) && data.articles.length > 0) {
        setStories(data.articles);
        try {
          sessionStorage.setItem(`wc_news_articles_${language}`, JSON.stringify({ articles: data.articles, ts: Date.now() }));
        } catch { /* ignore */ }
      } else {
        setStories(WORLD_CUP_STORIES);
      }
    } catch {
      setStories(WORLD_CUP_STORIES);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStories();
    // Re-fetch when language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const displayStories = stories.length > 0 ? stories : WORLD_CUP_STORIES;
  const grouped = groupByDate(displayStories);

  return (
    <AppLayout activeRoute={pathname}>
      <div className="min-h-screen px-4 py-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Newspaper size={20} className="text-orange-400" />
              <span className="text-xs font-bold uppercase tracking-widest text-orange-400">{t.news.worldCup}</span>
            </div>
            <h1 className="text-3xl font-extrabold text-white mb-2">{t.news.title}</h1>
            <p className="text-sm" style={{ color: 'hsl(222 15% 55%)' }}>
              {t.news.subtitle}
            </p>
            {displayStories.length > 0 && (
              <div className="flex items-center gap-1.5 mt-2">
                <Archive size={12} className="text-orange-400" />
                <span className="text-xs font-medium" style={{ color: 'hsl(222 15% 50%)' }}>
                  {displayStories.length} {displayStories.length !== 1 ? t.news.archiveCountPlural : t.news.archiveCount}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={() => fetchStories(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl transition-all hover:bg-white/5 flex-shrink-0 mt-1"
            style={{ color: 'hsl(25 95% 60%)', border: '1px solid hsl(222 25% 18%)' }}
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? t.news.refreshing : t.news.refresh}
          </button>
        </div>

        {/* Stories — grouped by fetch date */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 mb-12">
            <svg className="animate-spin w-9 h-9 text-orange-400" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-sm font-medium" style={{ color: 'hsl(222 15% 55%)' }}>{t.news.loading}</span>
          </div>
        ) : grouped.length > 0 ? (
          <div className="flex flex-col gap-10 mb-12">
            {grouped.map(({ label, stories: groupStories }) => (
              <div key={label}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-5 rounded-full bg-orange-500 flex-shrink-0" />
                  <span className="text-sm font-bold text-white">{label}</span>
                  <div className="flex-1 h-px" style={{ background: 'hsl(222 25% 18%)' }} />
                  <span className="text-xs font-medium" style={{ color: 'hsl(222 15% 45%)' }}>
                    {groupStories.length} {groupStories.length === 1 ? t.news.story : t.news.stories}
                  </span>
                </div>
                <div className="flex flex-col gap-5">
                  {groupStories.map((story) => (
                    <StoryCard key={story.id} story={story} isRead={readSlugs.has(story.slug)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-5 mb-12">
            <div className="text-center py-16 text-slate-500 text-sm">{t.news.noStories}</div>
          </div>
        )}

        {/* History Log */}
        <div
          className="rounded-2xl p-6"
          style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-orange-400" />
              <h2 className="text-base font-bold text-white">{t.news.readingHistory}</h2>
              {history.length > 0 && (
                <span
                  className="text-xs font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: 'hsl(25 95% 53% / 0.15)', color: 'hsl(25 95% 65%)' }}
                >
                  {history.length}
                </span>
              )}
            </div>
            {history.length > 0 && (
              <button
                onClick={clearHistory}
                className="flex items-center gap-1.5 text-xs font-semibold transition-colors hover:text-red-400"
                style={{ color: 'hsl(222 15% 50%)' }}
              >
                <Trash2 size={13} /> {t.news.clearHistory}
              </button>
            )}
          </div>

          {history.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen size={32} className="mx-auto mb-3 opacity-30 text-slate-400" />
              <p className="text-sm" style={{ color: 'hsl(222 15% 50%)' }}>
                {t.news.noHistory}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {history.map((entry, i) => (
                <Link
                  key={`${entry.slug}-${i}`}
                  href={`/news/${entry.slug}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl transition-colors hover:bg-white/5 group"
                  style={{ border: '1px solid transparent' }}
                >
                  <img
                    src={entry.image}
                    alt={entry.alt}
                    className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate group-hover:text-orange-400 transition-colors">
                      {entry.title}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'hsl(222 15% 50%)' }}>
                      {entry.visitedAt}
                    </p>
                  </div>
                  <ArrowRight size={14} className="text-slate-600 group-hover:text-orange-400 transition-colors flex-shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
